<template>
  <div class="history">
    <v-base-card>
      <template v-slot:heading>
        プリセット一覧
      </template>

      <template v-slot:content>
        <v-row class="mb-0 pb-0">
          <!-- 絞り込みは各列の見出しから行う（条件は別画面から戻っても残る） -->
          <v-col cols="9" class="d-flex align-center" style="gap: 12px">
            <span class="text-body-2">{{ filteredItems.length.toLocaleString() }}件 / 全{{ listItems.length.toLocaleString() }}件</span>
            <v-btn small outlined :disabled="!activeFilterCount && !sortBy.length" @click="clearAllFilters">
              <v-icon left small>mdi-filter-remove-outline</v-icon>フィルター・並び順をクリア
            </v-btn>
            <span class="text-caption grey--text">列の見出しをクリックすると、並べ替えと絞り込みができます</span>
          </v-col>
          <v-col cols="2" class="d-flex align-center" style="gap: 8px">
            <v-btn
              color="primary"
              small
              :loading="exporting"
              @click="exportXlsx"
            >
              XLSX出力
            </v-btn>
            <v-btn
              small
              color="#4a81aa"
              dark
              @click="showImportArea = !showImportArea"
            >
              XLSX取込
            </v-btn>
          </v-col>
          <v-col cols="1" class="d-flex align-center">
            <v-btn
              color="primary"
              small
              @click="createNew"
            >
              新規作成
            </v-btn>
          </v-col>
        </v-row>
        <v-expand-transition>
          <div v-if="showImportArea" class="import-area mx-2 mb-3">
            <!-- 取込結果 -->
            <div v-if="importResults.length" class="mb-3">
              <div
                v-for="(result, idx) in importResults"
                :key="idx"
                class="import-result-item"
                :class="{ 'mb-2': idx < importResults.length - 1 }"
              >
                <div class="d-flex align-center">
                  <v-icon x-small class="mr-1" :color="result.success ? 'green' : 'red'">
                    {{ result.success ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  <span class="text-caption font-weight-medium">{{ result.filename }}</span>
                  <span class="text-caption ml-2">{{ result.message }}</span>
                  <v-spacer></v-spacer>
                  <v-btn icon x-small @click="removeResult(idx)"><v-icon x-small>mdi-close</v-icon></v-btn>
                </div>
              </div>
            </div>
            <!-- ドロップゾーン -->
            <div
              class="dropzone-area"
              :class="{ 'dropzone-active': dragging }"
              @dragover.prevent="dragging = true"
              @dragenter.prevent="dragging = true"
              @dragleave.prevent="dragging = false"
              @drop.prevent="onDrop"
              @click="$refs.fileInput.click()"
            >
              <input
                ref="fileInput"
                type="file"
                accept=".xlsx,.xls"
                multiple
                style="display: none"
                @change="onFileSelect"
              >
              <div v-if="!importFiles.length" class="dropzone-placeholder">
                <v-icon size="28" color="grey">mdi-file-upload-outline</v-icon>
                <div class="text-body-2 grey--text mt-1">XLSXをドロップまたはクリック</div>
              </div>
              <div v-else>
                <div
                  v-for="(file, i) in importFiles"
                  :key="i"
                  class="dropzone-selected d-flex align-center"
                  :class="{ 'mt-1': i > 0 }"
                >
                  <v-icon small class="mr-2" color="#4a81aa">mdi-file-excel</v-icon>
                  <span class="text-body-2">{{ file.name }}</span>
                  <v-spacer></v-spacer>
                  <v-btn icon x-small @click.stop="removeFile(i)"><v-icon x-small>mdi-close</v-icon></v-btn>
                </div>
              </div>
            </div>
            <v-btn
              small
              color="#4a81aa"
              dark
              class="mt-2"
              :loading="uploading"
              :disabled="!importFiles.length"
              @click="startImport"
            >
              取込開始（{{ importFiles.length }}ファイル）
            </v-btn>
          </div>
        </v-expand-transition>
        <v-data-table
          :headers="headers"
          :items="filteredItems"
          :loading="loading"
          loading-text="Loading... Please wait"
          fixed-header
          :height="'calc(100vh - 300px)'"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :page.sync="tablePage"
          :items-per-page.sync="itemsPerPage"
          :footer-props="{ 'items-per-page-options': [50, 100, 200, -1] }"
        >
          <!-- 列の見出し: Excelのオートフィルターのように並べ替え・絞り込みができる -->
          <template v-for="column in filterColumns" v-slot:[`header.${column.value}`]>
            <v-column-filter-menu
              :key="column.value"
              :column="column"
              :items="listItems"
              :filter="columnFilters[column.value]"
              :sort-direction="columnSortDirection(column.value)"
              @filter="setColumnFilter(column.value, $event)"
              @sort="setColumnSort(column.value, $event)"
            />
          </template>
          <!-- 進学教材プリセットの年度(R8/R9の出し分けに使う)。未設定はR8として扱う -->
          <template #[`item.fiscal_year_western`]="{ item }">
            <template v-if="item.is_shingaku">{{ item.fiscal_year_western ? `R${item.fiscal_year_western - 2018}` : '未設定' }}</template>
          </template>
          <template #[`item.status`]="{ item }">
            <v-chip :color="item.status === 'published' ? 'green' : 'grey'" dark small>
              {{ item.status === 'published' ? '公開中' : '下書き' }}
            </v-chip>
          </template>
          <template #[`item.has_pdf`]="{ item }">
            <v-icon v-if="item.has_pdf" color="green" small>mdi-check-circle</v-icon>
            <v-icon v-else color="grey" small>mdi-close-circle</v-icon>
          </template>
          <template #[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>
          <template #[`item.updated_at`]="{ item }">
            {{ formatDate(item.updated_at) }}
          </template>
          <template #[`item.edit`]="{ item }">
            <v-btn icon @click.stop="edit(item)">
              <v-icon small dark>mdi-cog-outline</v-icon>
            </v-btn>
          </template>
          <template #[`item.delete`]="{ item }">
            <v-btn icon @click.stop="openDeleteConfirmDialog(item)">
              <v-icon dark>mdi-delete</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </template>
    </v-base-card>

    <v-confirm-dialog
      ref="confirm"
      title="プリセット削除"
      :message="deleteMessage"
      buttonMessage="削除"
      @confirm="confirmDeletion"
    />
  </div>
</template>

<script>
import VBaseCard from '@/components/molecules/BaseCard'
import VConfirmDialog from '@/components/atoms/ConfirmDialog'
import VColumnFilterMenu from '@/components/molecules/ColumnFilterMenu'
import columnFilterList from '@/mixins/columnFilterList'

export default {
  components: {
    VBaseCard,
    VConfirmDialog,
    VColumnFilterMenu,
  },
  mixins: [columnFilterList('admin-presets-list')],
  data () {
    return {
      // 並べ替えは列見出しのメニューから行うため、見出しクリックでの並べ替えは無効にする
      headers: [
        { text: 'コード', align: 'start', value: 'code', sortable: false },
        { text: 'プリセット名', align: 'start', value: 'name', sortable: false },
        { text: '教科', align: 'center', value: 'subject_name', sortable: false },
        { text: '学年', align: 'center', value: 'grade_name', sortable: false },
        { text: 'テスト時期', align: 'center', value: 'period_name', sortable: false },
        { text: '年度', align: 'center', value: 'fiscal_year_western', sortable: false },
        { text: 'ステータス', align: 'center', value: 'status', sortable: false },
        { text: 'PDF', align: 'center', value: 'has_pdf', sortable: false },
        { text: '登録日', align: 'center', value: 'created_at', sortable: false },
        { text: '更新日', align: 'center', value: 'updated_at', sortable: false },
        { text: '編集', align: 'center', value: 'edit', sortable: false },
        { text: '削除', align: 'center', value: 'delete', sortable: false },
      ],
      deletedPreset: { id: null, name: '' },
      loading: false,
      exporting: false,
      showImportArea: false,
      importFiles: [],
      importResults: [],
      uploading: false,
      dragging: false,
    }
  },

  async mounted() {
    await this.getPresets()
  },

  computed: {
    deleteMessage() {
      return `${this.deletedPreset.name} (${this.deletedPreset.code}) を削除します。よろしいですか。`
    },
    // 列ごとの絞り込み方（select: 値のチェックリスト / text: 文字を含む / date: 期間）
    filterColumns() {
      const column = (value, type, label) => ({ ...this.headers.find(h => h.value === value), type, label })
      return [
        column('code', 'text', item => item.code),
        column('name', 'text', item => item.name),
        column('subject_name', 'select', item => item.subject_name),
        column('grade_name', 'select', item => item.grade_name),
        column('period_name', 'select', item => item.period_name),
        // 年度は進学教材プリセットだけ表示する（日常教材は空白）
        column('fiscal_year_western', 'select', item => (item.is_shingaku ? (item.fiscal_year_western ? `R${item.fiscal_year_western - 2018}` : '未設定') : '')),
        column('status', 'select', item => (item.status === 'published' ? '公開中' : '下書き')),
        column('has_pdf', 'select', item => (item.has_pdf ? 'あり' : 'なし')),
        column('created_at', 'date', item => item.created_at),
        column('updated_at', 'date', item => item.updated_at),
      ]
    },
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      return dateStr.slice(0, 10)
    },
    async getPresets() {
      this.loading = true
      const res = await this.$axios('/api/v1/admin/presets?all=1')
      this.listItems = res.data.items
      this.loading = false
      this.applyRestoredPage()
    },
    openDeleteConfirmDialog(preset) {
      this.deletedPreset = preset
      this.$refs.confirm.open()
    },
    async confirmDeletion() {
      const url = `api/v1/admin/presets/${this.deletedPreset.id}`
      await this.$axios.delete(url)
      this.$toasted.global.success({ message: `${this.deletedPreset.name}を削除しました` })
      this.getPresets()
    },
    async exportXlsx() {
      this.exporting = true
      try {
        // 絞り込み中は表示中の行だけを出力する（IDが多くなるためPOSTで送る）
        const res = this.activeFilterCount
          ? await this.$axios.post('/api/v1/admin/presets/export', { ids: this.filteredItems.map(item => item.id) }, { responseType: 'blob' })
          : await this.$axios('/api/v1/admin/presets/export', { responseType: 'blob' })
        const contentDisposition = res.headers['content-disposition']
        let filename = 'プリセット一覧.xlsx'
        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?([^;\n]+)/i)
          if (match) filename = decodeURIComponent(match[1].replace(/["']/g, ''))
        }
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$toasted.global.error({ message: 'エクスポートに失敗しました' })
      } finally {
        this.exporting = false
      }
    },
    edit(preset) {
      this.$router.push(`/admin/preset/${preset.id}`)
    },
    createNew() {
      this.$router.push('/admin/preset/new')
    },
    onDrop(e) {
      this.dragging = false
      const files = Array.from(e.dataTransfer.files).filter(f => /\.xlsx?$/i.test(f.name))
      if (files.length === 0) {
        this.$toasted.global.error({ message: 'XLSXファイルを選択してください' })
        return
      }
      this.importFiles = [...this.importFiles, ...files]
    },
    onFileSelect(e) {
      const files = Array.from(e.target.files).filter(f => /\.xlsx?$/i.test(f.name))
      this.importFiles = [...this.importFiles, ...files]
      e.target.value = ''
    },
    removeFile(index) {
      this.importFiles.splice(index, 1)
    },
    removeResult(index) {
      this.importResults.splice(index, 1)
    },
    async startImport() {
      if (!this.importFiles.length) return
      this.uploading = true
      try {
        for (const file of this.importFiles) {
          try {
            const formData = new FormData()
            formData.append('file', file)
            const res = await this.$axios.post('/api/v1/admin/presets/import', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 600000,
            })
            const msg = res.data.messages
            this.importResults.push({
              filename: file.name,
              message: msg,
              success: msg === '成功',
            })
          } catch (e) {
            const errMsg = e.response && e.response.data ? e.response.data.messages : e.message
            this.importResults.push({
              filename: file.name,
              message: errMsg || '取り込みに失敗しました',
              success: false,
            })
          }
        }
        this.importFiles = []
        this.getPresets()
      } finally {
        this.uploading = false
      }
    },
  }
}
</script>

<style lang="stylus" scoped>
.select
  font-size 11px !important

.import-area
  padding 12px
  background #fafafa
  border 1px solid #e0e0e0
  border-radius 4px

.dropzone-area
  border 2px dashed #bdbdbd
  border-radius 6px
  padding 20px
  text-align center
  cursor pointer
  transition border-color 0.2s, background 0.2s

  &:hover
    border-color #4a81aa
    background #f0f6fb

  &.dropzone-active
    border-color #4a81aa
    background #e3f0fa

.dropzone-selected
  text-align left

.import-result-item
  padding 8px
  background #fff
  border 1px solid #e8e8e8
  border-radius 4px
</style>
